import React, { useState, useContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  useNetworkResponse,
  useAutoDisposable,
} from "@activehub/common-components-spa-resource";
import { searchUserByUserName, searchUsersV2 } from "../../network/ApiService";
 
export function useAccessCodeUserDetails() {
  const AHPMRIDS = [
    "urn:pearson:gps:productmodelregion:258009e1-960f-4e96-972c-21e6f2d15105",
    "urn:pearson:gps:productmodelregion:efff9ce8-de08-459c-9ce2-d866b34ca45d",
    "urn:pearson:gps:productmodelregion:e0fe76ad-bc5a-4dc1-8c7c-caf636e7e2a3",
    "urn:pearson:gps:productmodelregion:9b92df41-f614-408a-8e49-d8a255a7b52a",
  ];
  const history = useHistory();
  const { pushSubscription } = useAutoDisposable();
 
  // Network responses
  const userSearchResponse = useNetworkResponse();
  const userSearchInRegion = useNetworkResponse();
  const createRegisterUserResponse = useNetworkResponse();
 
  // State for form values
  const [formValues, setFormValues] = useState({
    email: "",
    firstname: "",
    lastname: "",
    username: "",
    existUserId: null,
    existUserIdTemp: null,
    existNonOrgUserId: null,
  });
 
  // State for email validation
  const [isEmailValidated, updateValidation] = useState(true);
  const [emailErrorType, setType] = useState(
    "Please enter a valid email address"
  );
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
 
  // State for first name validation
  const [isFirstnameValidated, updateNameValidation] = useState(true);
  const [fnameErrorType, setfnameErrorType] = useState(
    "Please enter a first name"
  );
 
  // State for last name validation
  const [isLastnameValidated, updateLastNameValidation] = useState(true);
  const [lnameErrorType, setlnameErrorType] = useState(
    "Please enter a last name"
  );
  const [errMsgTooltip, setErrMsgTooltip] = useState(false);

  // State to track if fields have content when onBlur occurs
  const [fieldInteractionState, setFieldInteractionState] = useState({
    email: { hasContent: false, showErrors: false },
    firstname: { hasContent: false, showErrors: false },
    lastname: { hasContent: false, showErrors: false }
  });

  // State to track original existing user data for comparison
  const [originalExistingUser, setOriginalExistingUser] = useState({
    email: null,
    firstname: null,
    lastname: null,
    existUserId: null
  });

  // State to track previous email for clearing names on email change
  const [previousEmail, setPreviousEmail] = useState(null);
 
  // Form input handler
  const handleInput = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (value.trim() !== "") {
      setFieldInteractionState(prev => ({
        ...prev,
        [name]: { hasContent: true, showErrors: prev[name]?.showErrors || false }
      }));
    } else {
      setFieldInteractionState(prev => ({
        ...prev,
        [name]: { hasContent: false, showErrors: prev[name]?.showErrors || false }
      }));
    }
 
    if (name === "email") {
      // Handle when email becomes empty (user cleared it with backspace)
      if (!value || value.trim() === "") {
        // Clear error message immediately when email is removed
        setShowEmailSuccess(false);
        setType("Please enter a valid email address");
        // Clear any existing error display
        setFieldInteractionState(prev => ({
          ...prev,
          email: { hasContent: false, showErrors: false }
        }));
        if(isExistingUser && originalExistingUser.existUserId){
          setFormValues((prev)=>({
            ...prev,
            firstname: "",
            lastname: "",
            username: "",
            existUserId: null,
            existUserIdTemp: null,
            existNonOrgUserId: null,
          }))
          setIsExistingUser(false)

          setOriginalExistingUser({
            email : null,
            firstname : null,
            lastname : null,
            existUserId : null
          });
          setPreviousEmail(null);
        }
      } else if (!isEmailValidated && value) {
        // Clear error state and show success if currently invalid and user types valid format
        let regexpem = /^(([^<>()[\]\\\/.,;:`!*#%&|?=^~{}$\s@"]+(\.[^<>()[\]\\\/.,;:`!*#%&|?=^~$\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (regexpem.test(value)) {
          // Show success immediately for good UX - API call on blur will override if needed
          handleUserNameValidation("", true);
          // Ensure field interaction state allows success message to show
          setFieldInteractionState(prev => ({
            ...prev,
            email: { hasContent: true, showErrors: false }
          }));
        }
      }
    }

    if (name === "firstname") {
      if (!isFirstnameValidated && value) {
        let regexp = /^[ A-Za-z0-9-'.àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ]*$/;
        if (regexp.test(value) && value.trim() !== "") {
          handleFirstNameValidation("", true);
        }
      }
    }
    
    if (name === "lastname") {
      if (!isLastnameValidated && value) {
        let regexp = /^[ A-Za-z0-9-'.àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ]*$/;
        if (regexp.test(value) && value.trim() !== "") {
          handleLastNameValidation("", true);
        }
      }
    }
  };
 
    // Copy email to username
  const copyUserMailToUserName = useCallback(() => {
    setFormValues((prev) => ({ ...prev, username: prev.email }));
  }, []);

  // Email validation handler
  const handleUserNameValidation = useCallback((message, isValidated = false) => {
    if (isValidated) {
      setType("This is a valid email");
      setShowEmailSuccess(true);
      updateValidation(true);
      copyUserMailToUserName();
      setErrMsgTooltip(false);
      return;
    } else {
      setType(message);
      setShowEmailSuccess(false);
      updateValidation(false);
      setFormValues((prev) => ({ ...prev, username: "" }));
    }
  }, [copyUserMailToUserName]);
 
  // First name validation handler
  const handleFirstNameValidation = (
    warnmessage,
    isFirstnameValidated = false
  ) => {
    setfnameErrorType(warnmessage);
    updateNameValidation(isFirstnameValidated);
  };
 
  // Last name validation handler
  const handleLastNameValidation = (
    lastwarnmessage,
    isLastnameValidated = false
  ) => {
    setlnameErrorType(lastwarnmessage);
    updateLastNameValidation(isLastnameValidated);
  }; 

  const clearExistingUserData = useCallback(() => {
    setIsExistingUser(false);
    setFormValues((prev) => ({
      ...prev,
      firstname: "",
      lastname: "",
      username: "",
      existUserId: null,
      existUserIdTemp: null,
      existNonOrgUserId: null,
    }));
    updateNameValidation(true);
    setfnameErrorType("Please enter a first name");
    updateLastNameValidation(true);
    setlnameErrorType("Please enter a last name");
    
    setFieldInteractionState(prev => ({
      ...prev,
      firstname: { hasContent: false, showErrors: false },
      lastname: { hasContent: false, showErrors: false }
    }));
    
    setOriginalExistingUser({
      email: null,
      firstname: null,
      lastname: null,
      existUserId: null
    });
    
    setPreviousEmail(null);
  }, []);

  const forceFieldInteractionState = useCallback((fieldName) => {
    setFieldInteractionState(prev => ({
      ...prev,
      [fieldName]: { hasContent: true, showErrors: true }
    }));
  }, []);

  const validateNameField = useCallback((fieldName, value, forceShowErrors, validationHandler, errorMessage) => {
    if (!forceShowErrors) {
      setFieldInteractionState(prev => ({
        ...prev,
        [fieldName]: { hasContent: prev[fieldName].hasContent, showErrors: true }
      }));
    }
    
    let regexp = /^[ A-Za-z0-9-'.àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ]*$/;
    const fieldState = fieldInteractionState[fieldName];
    const displayName = fieldName === 'firstname' ? 'first name' : 'last name';
    
    if (!regexp.test(value)) {
      if (fieldState.hasContent || forceShowErrors) {
        validationHandler(
          `A ${displayName} cannot contain any special characters other than: '.-`
        );
        if (forceShowErrors) {
          forceFieldInteractionState(fieldName);
        }
      }
    } else if (!value) {
      if (fieldState.hasContent || forceShowErrors) {
        validationHandler(`Please enter a ${displayName}`);
        if (forceShowErrors) {
          forceFieldInteractionState(fieldName);
        }
      } else {
        if (fieldName === 'firstname') {
          updateNameValidation(true);
          setfnameErrorType("Please enter a first name");
        } else {
          updateLastNameValidation(true);
          setlnameErrorType("Please enter a last name");
        }
      }
    } else {
      validationHandler("", true);
    }
  }, [fieldInteractionState, forceFieldInteractionState]);
 
  // Search user in IES
  const searchUserInIES = useCallback(
    ({ userName }) => {
      userSearchResponse.dispatch({ type: "STARTED" });
      const subscription = searchUserByUserName(userName).subscribe(
        (response) => {
          userSearchResponse.dispatch({ type: "SUCCESS", payload: response });
          const updatedFormValues = {
            existUserId: response.userId,
            existUserIdTemp: response.userId,
            username: response.userName,
            firstname:response.firstName || "",
            lastname: response.lastName || "",
          };
          setFormValues((prev) => ({ ...prev, ...updatedFormValues }));
          handleFirstNameValidation("", true);
          handleLastNameValidation("", true);

          setFieldInteractionState(prev => ({
            ...prev,
            email: { hasContent: true, showErrors: true }
          }));
        },
        (error) => {
          const { status } = error;
          userSearchResponse.dispatch({ type: "ERROR", payload: error });
          if (status !== 404) {
            console.error("Error searching user in IES:", error);
            setFormValues((prev) => ({
              ...prev,
              username: userName,
              existUserId: null,
              existUserIdTemp: null,
              // Preserve existing firstname and lastname values
            }));
            setIsExistingUser(false); // Not an existing user
            return;
          }
          // Email was already set as valid before API call
          // No need to call handleUserNameValidation again
          
          // Ensure valid email message shows by setting field interaction state
          setFieldInteractionState(prev => ({
            ...prev,
            email: { hasContent: true, showErrors: false }
          }));
          
          setFormValues((prev) => ({
            ...prev,
            username: userName,
            existUserId: null,
            existUserIdTemp: null,
            // Preserve existing firstname and lastname values for new users
          }));
          setIsExistingUser(false); // Not an existing user
          setPreviousEmail(userName);
        }
      );
 
      pushSubscription(subscription);
    },
    [handleUserNameValidation, handleFirstNameValidation, handleLastNameValidation]
  );
 
  // Search user in region (similar to AddSingleUserAH)
  const searchUserInRegion = useCallback(
    ({ userName }) => {
      if (!userName) return;
 
      userSearchInRegion.dispatch({ type: "STARTED" });
      const payload = {
        filter: {
          regionId: "urn:pearson:gps:region:gb",
          userName: userName,
        },
      };
 
      const subscription = searchUsersV2(null, payload).subscribe(
        (response) => {
          const responseData = response?.data || [];
          userSearchInRegion.dispatch({
            type: "SUCCESS",
            payload: responseData,
          });
 
          if (Array.isArray(responseData) && responseData.length === 0) {
            searchUserInIES({ userName });
            return;
          }
 
          const userData = responseData[0];
          const { organizations, organizationGroups } = userData;
 
          let updatedFormValues = {
            username: userData?.userName || null,
            firstname: userData?.firstName || "",
            lastname: userData?.lastName || "",
            existUserId: userData?.userId || null,
            existUserIdTemp: userData?.userId || null,
            existNonOrgUserId: null,
          };
 
          if (
            (organizations == null && organizationGroups ==  null) ||
            (Array.isArray(organizations) && organizations.length === 0) ||
            (Array.isArray(organizationGroups) && organizationGroups.length === 0)
          ) {
            setIsExistingUser(false);
            updatedFormValues = {
              ...updatedFormValues,
              existNonOrgUserId: userData?.userId || null,
            };
            setFormValues((prev) => ({ ...prev, ...updatedFormValues }));
            return;
          } else if (organizations || organizationGroups){
            const isUserPartOfActivehubOrg = organizations?.filter((org) =>
              org?.productModelRegion?.some((r) => AHPMRIDS.includes(r))
            );
 
            if (
              Array.isArray(isUserPartOfActivehubOrg) &&
              isUserPartOfActivehubOrg.length === 0
            ) {
              if (userData.region.regionId === "urn:pearson:gps:region:uk") {
                searchUserInIES({ userName });
                return;
              }
              return;
            }
 
            const errorMessage =
              "This email is already associated with an ActiveHub account. Please sign in.";
            handleUserNameValidation(errorMessage, false);
            
            // Ensure error message shows by setting field interaction state
            setFieldInteractionState(prev => ({
              ...prev,
              email: { hasContent: true, showErrors: true }
            }));
          }
 
          setFormValues((prev) => ({ ...prev, ...updatedFormValues }));
          handleFirstNameValidation("", true);
          handleLastNameValidation("", true);
          
          setIsExistingUser(true);
          setOriginalExistingUser({
            email: userName,
            existUserId: updatedFormValues.existUserId
          });
          setPreviousEmail(userName);
        },
        (error) => {
          const { status } = error;
          userSearchInRegion.dispatch({ type: "ERROR", payload: error });
          if (status !== 404) {
            console.log("search user");
            setFormValues((prev) => ({
              ...prev,
              username: userName,
              existUserId: null,
              existUserIdTemp: null,
              // Preserve existing firstname and lastname values
            }));
            console.error("Error searching user in region:", error);
            searchUserInIES({ userName });
            return;
          }
 
          searchUserInIES({ userName });
        }
      );
      pushSubscription(subscription);
    },
    [searchUserInIES, handleUserNameValidation, handleFirstNameValidation, handleLastNameValidation]
  );
 
  // Email validation function
  const emailValidation = useCallback(
    (valueOrEvent, forceShowErrors = false) => {
      if((!(formValues.firstname && formValues.lastname) && !formValues.email)||(!formValues.email)){
        setFieldInteractionState(prev => ({
            ...prev,
            email: { hasContent: false, showErrors: true }
          }));
        return
      }
      let regexpem =
        /^(([^<>()[\]\\\/.,;:`!*#%&|?=^~{}$\s@"]+(\.[^<>()[\]\\\/.,;:`!*#%&|?=^~$\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      let email = formValues.email;
      if (!forceShowErrors) {
        setFieldInteractionState(prev => ({
          ...prev,
          email: { hasContent: prev.email.hasContent, showErrors: true }
        }));
      }
      if (isExistingUser && originalExistingUser.email && email !== originalExistingUser.email) {
        clearExistingUserData();
      }

      if (regexpem.test(email)) {
        if (previousEmail && email !== previousEmail && email.trim() !== "" && previousEmail.trim() !== "") {
        
          updateNameValidation(true);
          setfnameErrorType("Please enter a first name");
          updateLastNameValidation(true);
          setlnameErrorType("Please enter a last name");
          setFieldInteractionState(prev => ({
            ...prev,
            firstname: { hasContent: false, showErrors: false },
            lastname: { hasContent: false, showErrors: false }
          }));
        }

        setPreviousEmail(email);
        
        // Set as valid first (will be overridden if user exists)
        handleUserNameValidation("", true);
        
        // Make API call to check if user exists
        searchUserInRegion({
          userName: formValues.email,
        });
      } else {
        const atLocation = formValues?.email?.lastIndexOf("@");
        const dotLocation = formValues?.email?.split("@")[1]?.lastIndexOf(".");
 
        if (!email) {
          if (fieldInteractionState.email.hasContent || forceShowErrors) {
            handleUserNameValidation(`Please enter a valid email address`);
            if (forceShowErrors) {
              forceFieldInteractionState('email');
            }
          } else {
            updateValidation(false); // Empty email should always be invalid
            setShowEmailSuccess(false);
            setType("Please enter a valid email address");
          }
          
          if ((isExistingUser || formValues.existUserId) && originalExistingUser.existUserId) {
            clearExistingUserData();
          } else if (isExistingUser || formValues.existUserId) {
            setIsExistingUser(false);
            setFormValues((prev) => ({
              ...prev,
              username: "",
              existUserId: null,
              existUserIdTemp: null,
              existNonOrgUserId: null,
            }));
          }
        } else if (atLocation === -1) {
          handleUserNameValidation(`The @ symbol is missing between ${email}.`);
          if (forceShowErrors) {
            forceFieldInteractionState('email');
          }
        } else if (atLocation === 0) {
          handleUserNameValidation(
            `The recipient name is missing before ${email}.`
          );
          if (forceShowErrors) {
            forceFieldInteractionState('email');
          }
        } else if (dotLocation === 0 || dotLocation === -1) {
          handleUserNameValidation(
            "Make sure the '.' is after the '@' and there is at least one char between them"
          );
          if (forceShowErrors) {
            forceFieldInteractionState('email');
          }
        } else if (email?.endsWith(".")) {
          handleUserNameValidation(
            "Make sure there is at least a single char after the '.'"
          );
          if (forceShowErrors) {
            forceFieldInteractionState('email');
          }
        } else if (!regexpem.test(email)) {
          handleUserNameValidation(
            "An username cannot contain any special characters other than: '.-@_+"
          );
          if (forceShowErrors) {
            forceFieldInteractionState('email');
          }
        } else {
          handleUserNameValidation("", true);
        }
      }
    },
    [formValues.email, searchUserInRegion, handleUserNameValidation, fieldInteractionState.email, isExistingUser, originalExistingUser, clearExistingUserData, forceFieldInteractionState, previousEmail]
  );
 
  const firstNameValidation = useCallback((valueOrEvent, forceShowErrors = false) => {
    const firstName = typeof valueOrEvent === 'string' ? valueOrEvent : formValues.firstname;
    
    validateNameField('firstname', firstName, forceShowErrors, handleFirstNameValidation);
  }, [formValues.firstname, validateNameField]);

  const lastNameValidation = useCallback((valueOrEvent, forceShowErrors = false) => {
    const lastName = typeof valueOrEvent === 'string' ? valueOrEvent : formValues.lastname;
    
    validateNameField('lastname', lastName, forceShowErrors, handleLastNameValidation);
  }, [formValues.lastname, validateNameField]);
 
  // Check if all required form data is filled and valid
  const isRequiredFormDataFilled = useMemo(() => {
    return (
      formValues.email &&
      formValues.firstname &&
      formValues.lastname &&
      isEmailValidated &&
      isFirstnameValidated &&
      isLastnameValidated
    );
  }, [formValues, isEmailValidated, isFirstnameValidated, isLastnameValidated]);
 
  // Check if form has any changes (dirty form)
  const isDirtyForm = useMemo(() => {
    const { email, firstname, lastname } = formValues;
    return !!(email || firstname || lastname);
  }, [formValues]);

  const hasValidationErrors = useMemo(() => {
    const emailError = (!isEmailValidated && fieldInteractionState.email.showErrors);
    const firstnameError = (!isFirstnameValidated && fieldInteractionState.firstname.showErrors);
    const lastnameError = (!isLastnameValidated && fieldInteractionState.lastname.showErrors);
    
    return emailError || firstnameError || lastnameError;
  }, [isEmailValidated, isFirstnameValidated, isLastnameValidated, fieldInteractionState]);
 
  const handleClearEmail = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      email: "",
    }));
    
    setShowEmailSuccess(false);
    updateValidation(true);
    setType("Please enter a valid email address");
    
    setFieldInteractionState(prev => ({
      ...prev,
      email: { hasContent: false, showErrors: false }
    }));
    
    if (isExistingUser && originalExistingUser.existUserId) {
      clearExistingUserData();
      setFormValues((prev) => ({
        ...prev,
        email: "",
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        email: "",
        username: "",
        existUserId: null,
        existUserIdTemp: null,
        existNonOrgUserId: null,
      }));
      setIsExistingUser(false);
      
      setOriginalExistingUser({
        email: null,
        firstname: null,
        lastname: null,
        existUserId: null
      });
      setPreviousEmail(null);
    }
  }, [isExistingUser, originalExistingUser, clearExistingUserData]);

  return {
    formValues,
    handleInput,
    handleClearEmail,
    emailValidation,
    firstNameValidation,
    lastNameValidation,
    // registerUser,
    errMsgTooltip,
    setErrMsgTooltip,
    isRequiredFormDataFilled,
    isDirtyForm,
    hasValidationErrors,
    isEmailValidated,
    emailErrorType,
    showEmailSuccess,
    isFirstnameValidated,
    fnameErrorType,
    isLastnameValidated,
    lnameErrorType,
    isSearchLoading:
      userSearchResponse.isLoading || userSearchInRegion.isLoading,
    isCreatingUser: createRegisterUserResponse.isLoading,
    isExistingUser,
    fieldInteractionState,
    originalExistingUser,
    setFieldInteractionState,
    updateValidation
  };
}
